"use client";

import { PortableText } from "@portabletext/react";
import { customRichTextComponent, richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { GroqImage } from "@/helpers/Image";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import Image from "next/image";
import arrow from "@/public/global/dotted-arrow.svg";
import { motion } from "framer-motion";
import { Fragment } from "react";
import Link from "next/link";
import AffiliateHeroItems from "@/components/pageComponents/global/AffiliateHeroItems";
import AffiliateHeroRecommendation from "@/components/pageComponents/global/AffiliateHeroRecommendation";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";

import logo from "@/public/affiliate-template/logo.svg";

import heroDots from "@/public/affiliate-template/hero-dots.png";
import connector1 from "@/public/affiliate-template/connector-1.svg";
import connector2 from "@/public/affiliate-template/connector-2.svg";
// import shadow from "@/public/comp_affiliate-v2/Shadow.png";
// import shadowM from "@/public/comp_affiliate-v2/ShadowM.png";
import angleLine from "@/public/affiliate-v2/angledLine.svg";
import line from "@/public/affiliate-v2/line.svg";
import { ChevronRight } from "lucide-react";

export default function Hero({ data, className }: { data: PageAffiliateSchema; className?: string }) {
  const isMobile = useIsMobile();
  return (
    <>
      <section
        id="hero-section"
        className={cn("relative overflow-clip   ", className)}>
        <div className="flex justify-center items-center w-full border-b py-[3rem] border-b-black/20 gap-[1.5rem]">
          <Image
            src={logo}
            alt=""
          />
          <ChevronRight />
          <p className="m-0 font-geist font-[600] text-[2.5rem] text-[#103037] leading-[116%]">
            {data?.lastPartner.name}
          </p>
        </div>
        <div className="relative overflow-clip w-[min(189.25rem,100%)] mx-auto pt-[5rem] pb-[3.25rem] lg:pb-[4.5rem]  ">
          <div className="flex relative z-10 flex-col justify-between lg:flex-row lg:items-center lg:px-0 gap-[4rem] px-[1.5rem] lg:gap-[10.5rem] lg:min-h-[96.5rem]">
            <div className="flex flex-col items-center lg:items-start w-[min(166.25rem,100%)] mt-[2rem] lg:mt-0 ">
              <motion.div
                className="w-[min(80rem,100%)] space-y-[1.5rem] mb-[4.5rem] lg:mb-0"
                initial={{ opacity: 0, x: -50, filter: "blur(10px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.3,
                  delay: 0.05,
                  ease: [0.25, 0.4, 0.25, 1],
                }}>
                <div className="italic text-black text-[2rem] leading-[1.5] font-[400] font-blacker">
                  {data?.heroChip1.text}
                </div>

                <div>
                  {data?.heroText && (
                    <div className="richtext richtext-start font-blacker mb-[3rem] prose-em:font-blacker prose-em:font-[400] prose-headings:text-black prose-headings:text-[5rem] prose-headings:tracking-[-0.15rem] lg:prose-headings:text-[8rem] lg:prose-headings:tracking-[-0.24rem]">
                      <PortableText
                        value={data.heroText}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {data?.heroContent && (
                    <div
                      className="richtext richtext-center richtext-start prose-p:text-[2rem] prose-p:leading-[150%] prose-p:font-[400]
                      prose-strong:font-[500] prose-strong:text-[#103037] prose-strong:text-[2.25rem] prose-p:text-[#1C1F22]/70">
                      <PortableText
                        value={data.heroContent}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {data?.useGlobalHeroItems ? (
                    <AffiliateHeroItems
                      items={data?.affiliateHeroItems?.items}
                      language={data?.heroItemsLanguage || data?.settings?.language}>
                      <AffiliateHeroRecommendation item={data?.heroRecommendation} />
                    </AffiliateHeroItems>
                  ) : (
                    data?.heroItems && (
                      <div className="flex flex-col gap-[1.375rem] my-[5rem] w-[min(52.125rem,100%)]">
                        {data.heroItems.map((item, index) => (
                          <Fragment key={"fheroItem" + index}>
                            <div
                              key={"heroItem" + index}
                              className="flex items-center gap-[1.5rem]">
                              {item?.icon && (
                                <Image
                                  src={item.icon}
                                  alt="icon"
                                  className="!w-[2.375rem] shrink-0"
                                />
                              )}
                              <span>{item.text}</span>
                            </div>
                            {index !== data.heroItems.length - 1 && <hr className="w-full border-t border-black/10" />}
                          </Fragment>
                        ))}
                      </div>
                    )
                  )}
                </div>

                <div className="w-full lg:w-fit">
                  <CTAButton
                    data={data.heroCTA}
                    className="w-full"
                  />
                </div>
              </motion.div>
            </div>

            <div className="relative  lg:h-[90.625rem] bg-[#F7F7F7] rounded-[0.75rem]  w-[min(97.375rem,100%)] overflow-clip shrink-0 h-[46.25rem]">
              <Image
                src={heroDots}
                alt=""
                className="object-cover absolute inset-0 w-full h-full z-[1] mix-blend-exclusion"
              />
              <Image
                src={connector1}
                alt=""
                className="absolute w-[9rem] left-[31.125rem] top-[12.125rem] lg:left-[60.75rem] lg:top-[24.625rem] lg:w-[17.625rem]"
              />
              {data?.heroImage && (
                <motion.div
                  initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{
                    duration: 0.3,
                    delay: 0.2,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  className="absolute lg:top-[5rem] top-[2.5rem] lg:left-[5rem] left-[2.5rem] z-[5] lg:w-[min(56rem,100%)] w-[28.5rem]">
                  <Image
                    src={data?.heroImage}
                    alt=""
                    quality={100}
                    className="w-full"
                  />
                </motion.div>
              )}

              <Image
                src={connector2}
                alt=""
                className="absolute w-[3.5rem] z-[7] bottom-[11rem] right-[20.25rem] lg:w-[7rem] lg:bottom-[22.25rem] lg:right-[39.5rem]"
              />

              {data.heroImageBadge1 && (
                <motion.div
                  initial={{ opacity: 0, x: -120, filter: "blur(10px)" }}
                  animate={{
                    opacity: 1,
                    x: 0,
                    filter: "blur(0px)",
                  }}
                  transition={{
                    duration: 0.3,
                    delay: 0.35,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  className="lg:w-[min(35.875rem,100%)] absolute lg:bottom-[5rem] bottom-[2.5rem] lg:right-[5rem] right-[2.5rem] z-[8] w-[18.25rem]">
                  <Image
                    src={data.heroImageBadge1}
                    alt=""
                    quality={100}
                    className="w-full"
                  />
                </motion.div>
              )}

              {data.heroTextBadge1 && (
                <div className="absolute text-white bg-[#236C58] rounded-[.5rem] font-geist text-[1rem] font-[500] top-[10rem] right-[4.5rem] py-[0.375rem] px-[0.75rem] lg:top-[20rem] lg:py-[1rem] lg:px-[1.75rem] lg:text-[2rem] lg:right-[5rem]">
                  {data.heroTextBadge1}
                </div>
              )}

              {data.heroTextBadge2 && (
                <div className="absolute bg-white border bottom-[10rem] left-[4.5rem] text-black/60 font-geist py-[0.375rem] px-[0.75rem] z-[6] rounded-[.5rem] font-[500] text-[1rem] lg:py-[1rem] lg:px-[1.75rem] lg:bottom-[20rem] lg:left-[5rem] lg:text-[2rem]">
                  {data.heroTextBadge2}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
